import { Link } from "react-router-dom";
import { useObserver, useSubtree } from "subtree.js/react";
import { BackButton, FormError, OnboardingLayout, PrimaryButton } from "../../components/onboarding/OnboardingLayout";
import { onboardingPath } from "../../core/primitives/onboarding";
import { OnboardingWebsiteActions, OnboardingWebsiteState } from "./onboarding-website-model";

const labelCls = "mb-1.5 block text-xs font-medium text-white";

function ChoiceCard({
  selected,
  onSelect,
  title,
  blurb,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  title: string;
  blurb: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={`rounded-row border p-3.5 transition-colors ${selected ? "border-accent" : "border-line"}`}>
      {/* The whole card is the control, so the hit area matches what looks
          clickable — the radio alone is a 16px target on a phone. */}
      <button type="button" onClick={onSelect} className="flex w-full items-start gap-2.5 text-left">
        <span
          className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[1.5px] ${
            selected ? "border-accent" : "border-line"
          }`}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-accent" />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium text-white">{title}</span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted">{blurb}</span>
        </span>
      </button>
      {selected && children ? <div className="mt-3 pl-[26px]">{children}</div> : null}
    </div>
  );
}

export function OnboardingWebsitePage() {
  const state = useSubtree(OnboardingWebsiteState);
  const actions = useSubtree(OnboardingWebsiteActions);
  const observer = useObserver();
  const storeId = observer.watch(state.storeId) ?? "";
  const claimWalkthrough = observer.watch(state.claimWalkthrough);
  const choice = observer.watch(state.choice);
  const websiteUrl = observer.watch(state.websiteUrl);
  const slug = observer.watch(state.slug);
  const contactEmail = observer.watch(state.contactEmail);
  const returnsPolicy = observer.watch(state.returnsPolicy);
  const hostedErrors = observer.watch(state.hostedErrors);
  const slugProblem = observer.watch(state.slugProblem);
  const hasMappableLocation = observer.watch(state.hasMappableLocation);
  const error = observer.watch(state.error);
  const busy = observer.watch(state.busy);

  return (
    <OnboardingLayout
      step="website"
      title="Where can customers find you?"
      description="Stripe asks every shop for a web address before it can take card payments — and checks it."
      storeId={storeId}
      claimWalkthrough={claimWalkthrough}
      footer={
        <div className="flex items-center justify-between gap-3">
          <BackButton onClick={actions.back} />
          <PrimaryButton type="button" onClick={actions.submit} disabled={busy || Boolean(slugProblem)}>
            {busy ? "Saving…" : "Continue"}
          </PrimaryButton>
        </div>
      }
    >
      <FormError message={error} />

      <div className="flex flex-col gap-2.5">
        <ChoiceCard
          selected={choice === "own"}
          onSelect={() => actions.setChoice("own")}
          title="I already have a website"
          blurb="Use your own site. This is what Stripe will check, so point it at the page that describes your shop."
        >
          <input
            type="url"
            inputMode="url"
            value={websiteUrl}
            onChange={(event) => actions.setWebsiteUrl(event.target.value)}
            placeholder="cardcorner.com"
            className="field"
            aria-label="Your website address"
          />
        </ChoiceCard>

        <ChoiceCard
          selected={choice === "hosted"}
          onSelect={() => actions.setChoice("hosted")}
          title="Create a page for my shop"
          blurb="We'll host a page with your shop's name, what you sell, and each of your locations with its address and hours."
        >
          <div className="flex flex-col gap-3.5">
            <div>
              <div className="flex items-center gap-1 rounded-btn border border-line bg-surface px-2.5 py-2">
                <span className="shrink-0 font-mono text-[11px] text-faint">/shop/</span>
                <input
                  value={slug}
                  onChange={(event) => actions.setSlug(event.target.value)}
                  placeholder="card-corner"
                  className="min-w-0 flex-1 bg-transparent font-mono text-[11px] text-white outline-none"
                  aria-label="Your shop page address"
                />
              </div>
              {slugProblem ? (
                <p className="field-error">{slugProblem}</p>
              ) : (
                <p className="mt-1.5 text-[11px] leading-relaxed text-faint">
                  This becomes your shop's public link. It can be changed later, but anywhere you've printed it stops
                  working.
                </p>
              )}
            </div>

            {/* Both fields below are required, and asked here rather than left
                to Settings: without them the page goes live for customers but
                Stripe will not accept it as the business website, and a merchant
                who is not asked now finds out when verification stalls.

                What the page says you sell is not among them — that is the
                description from step 1, and it belongs to exactly one screen. */}
            <div>
              <label htmlFor="contactEmail" className={labelCls}>
                Contact email
              </label>
              <input
                id="contactEmail"
                type="email"
                value={contactEmail}
                onChange={(event) => actions.setContactEmail(event.target.value)}
                placeholder="hello@cardcorner.com"
                className="field"
              />
              {hostedErrors.contactEmail && <p className="field-error">{hostedErrors.contactEmail}</p>}
            </div>

            <div>
              <label htmlFor="returnsPolicy" className={labelCls}>
                Returns &amp; refunds
              </label>
              <textarea
                id="returnsPolicy"
                value={returnsPolicy}
                onChange={(event) => actions.setReturnsPolicy(event.target.value)}
                rows={3}
                placeholder="Returns accepted within 14 days on unopened product, with a receipt."
                className="field resize-y"
              />
              {hostedErrors.returnsPolicy ? (
                <p className="field-error">{hostedErrors.returnsPolicy}</p>
              ) : (
                <p className="mt-1.5 text-[11px] leading-relaxed text-faint">
                  Card networks require this on any website used to verify a business. You can refine it later in
                  Settings.
                </p>
              )}
            </div>

            {/* Says where the missing half of the page comes from, so dropping
                the field from this screen does not read as dropping it from the
                page. */}
            <p className="text-[11px] leading-relaxed text-faint">
              What the page says you sell is your shop description, from{" "}
              <Link to={onboardingPath("shop", storeId, claimWalkthrough)} className="font-medium underline">
                step 1
              </Link>
              .
            </p>

            {/* The one requirement this screen cannot collect. Shown as soon as
                the branch is opened rather than on submit, so the merchant does
                not fill two fields before being sent back a step. */}
            {!hasMappableLocation && (
              <p className="note note-warn text-[11.5px] leading-relaxed">
                None of your locations has a street address yet, and a page without one won't be accepted as your
                business website.{" "}
                <Link to={onboardingPath("locations", storeId, claimWalkthrough)} className="font-medium underline">
                  Add an address
                </Link>
                .
              </p>
            )}
          </div>
        </ChoiceCard>
      </div>
    </OnboardingLayout>
  );
}
